import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

// FiCalculator doesn't exist in Feather, using FiCpu for calculation/engineering context
const { FiCpu, FiSettings, FiInfo } = FiIcons;

const EngineeringCalculator = () => {
  const [activeCalc, setActiveCalc] = useState('bearing-life');
  const [bearingInputs, setBearingInputs] = useState({
    dynamicLoad: '',
    appliedLoad: '',
    speed: ''
  });
  const [sealInputs, setSealInputs] = useState({
    shaftDiameter: '',
    housingBore: '',
    width: ''
  });

  const calculateBearingLife = () => {
    const C = parseFloat(bearingInputs.dynamicLoad);
    const P = parseFloat(bearingInputs.appliedLoad);
    const n = parseFloat(bearingInputs.speed);
    
    if (!C || !P || !n) return null;
    
    // L10h = (C/P)^3 * (10^6)/(60*n) für Kugellager
    const L10h = Math.pow(C/P, 3) * (1000000 / (60 * n));
    return Math.round(L10h);
  };

  const calculators = {
    'bearing-life': {
      title: 'Wälzlager-Lebensdauer (L10h)',
      description: 'Berechnung nach ISO 281',
      component: (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Dynamische Tragzahl C (N)
            </label>
            <input
              type="number"
              value={bearingInputs.dynamicLoad}
              onChange={(e) => setBearingInputs(prev => ({...prev, dynamicLoad: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder="z.B. 14000"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Äquivalente Belastung P (N)
            </label>
            <input
              type="number"
              value={bearingInputs.appliedLoad}
              onChange={(e) => setBearingInputs(prev => ({...prev, appliedLoad: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder="z.B. 3000"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Drehzahl n (min⁻¹)
            </label>
            <input
              type="number"
              value={bearingInputs.speed}
              onChange={(e) => setBearingInputs(prev => ({...prev, speed: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder="z.B. 1500"
            />
          </div>
          {calculateBearingLife() && (
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-semibold text-green-900 mb-2">Berechnungsergebnis:</h4>
              <p className="text-2xl font-bold text-green-800">
                L₁₀ₕ = {calculateBearingLife().toLocaleString()} Stunden
              </p>
              <p className="text-sm text-green-700 mt-1">
                Nominelle Lebensdauer bei 90% Zuverlässigkeit
              </p>
            </div>
          )}
        </div>
      )
    },
    'seal-selection': {
      title: 'Dichtungs-Dimensionierung',
      description: 'Standard-Abmessungen nach DIN 3760',
      component: (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Wellendurchmesser d (mm)
            </label>
            <input
              type="number"
              value={sealInputs.shaftDiameter}
              onChange={(e) => setSealInputs(prev => ({...prev, shaftDiameter: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder="z.B. 25"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Gehäusebohrung D (mm)
            </label>
            <input
              type="number"
              value={sealInputs.housingBore}
              onChange={(e) => setSealInputs(prev => ({...prev, housingBore: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder="z.B. 47"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Breite B (mm)
            </label>
            <input
              type="number"
              value={sealInputs.width}
              onChange={(e) => setSealInputs(prev => ({...prev, width: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder="z.B. 7"
            />
          </div>
          {sealInputs.shaftDiameter && sealInputs.housingBore && sealInputs.width && (
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-900 mb-2">Standardbezeichnung:</h4>
              <p className="text-xl font-bold text-blue-800 font-mono">
                {sealInputs.shaftDiameter} × {sealInputs.housingBore} × {sealInputs.width}
              </p>
              <div className="mt-3 text-sm text-blue-700">
                <p><strong>Empfohlene Materialien:</strong></p>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>NBR (Standard, -30°C bis +100°C)</li>
                  <li>FKM (Hochtemperatur, -20°C bis +200°C)</li>
                  <li>EPDM (Heißwasser, nicht für Öle)</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      )
    }
  };

  return (
    <motion.div
      className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
          <SafeIcon icon={FiCpu} className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">Ingenieur-Rechner</h2>
          <p className="text-gray-600">Auslegungstools für Maschinenelemente</p>
        </div>
      </div>

      <div className="flex space-x-1 mb-6 bg-gray-100 p-1 rounded-lg">
        {Object.entries(calculators).map(([key, calc]) => (
          <button
            key={key}
            onClick={() => setActiveCalc(key)}
            className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeCalc === key
                ? 'bg-red-600 text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
            }`}
          >
            {calc.title}
          </button>
        ))}
      </div>

      <div>
        <div className="mb-4">
          <h3 className="font-semibold text-gray-900">{calculators[activeCalc].title}</h3>
          <p className="text-sm text-gray-600">{calculators[activeCalc].description}</p>
        </div>
        
        {calculators[activeCalc].component}
      </div>

      <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex items-start space-x-3">
          <SafeIcon icon={FiInfo} className="w-5 h-5 text-gray-600 mt-0.5" />
          <div>
            <h4 className="font-semibold text-gray-900 mb-1">Hinweise zur Berechnung</h4>
            <p className="text-sm text-gray-600">
              Die Berechnungen basieren auf Standardformeln und Normen. 
              Für kritische Anwendungen empfehlen wir eine detaillierte 
              Auslegung unter Berücksichtigung aller Betriebsparameter.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default EngineeringCalculator;